// Projects — building-level grouping of property units. Backed by the
// /api/v1/projects resource (list-with-stats, CRUD, next-code). The grouped
// "Dự án" view and the wizard project-picker both read from here.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "@/lib/api-client";
import { propertyKeys } from "@/features/property/api";
import type { PropertyListQuery } from "@/types/property";

/**
 * A project (building) with its shared fields + aggregate unit stats. Unit
 * stats are 0 for an empty project. `buildingName` on units is a synced copy of
 * `name`; drill into a project via `?projectId=`.
 */
export interface Project {
  id: number;
  name: string;
  wardCode: string | null;
  streetAddress: string | null;
  totalFloors: number | null;
  basements: number | null;
  completionYear: number | null;
  serviceFee: number | null;
  parkingFeeCar: number | null;
  parkingFeeMotorbike: number | null;
  codePrefix: string | null;
  unitCount: number;
  saleCount: number;
  rentCount: number;
  availableCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Editable fields — the create/update body (all optional on update). */
export interface ProjectInput {
  name?: string;
  wardCode?: string | null;
  streetAddress?: string | null;
  totalFloors?: number | null;
  basements?: number | null;
  completionYear?: number | null;
  serviceFee?: number | null;
  parkingFeeCar?: number | null;
  parkingFeeMotorbike?: number | null;
  codePrefix?: string | null;
}

export const projectKeys = {
  all: ["projects"] as const,
  lists: () => [...projectKeys.all, "list"] as const,
  list: (query: PropertyListQuery) => [...projectKeys.lists(), query] as const,
  details: () => [...projectKeys.all, "detail"] as const,
  detail: (id: number) => [...projectKeys.details(), id] as const,
  nextCode: (id: number) => [...projectKeys.all, "next-code", id] as const,
};

function buildProjectsPath(query: PropertyListQuery): string {
  const params = new URLSearchParams();
  if (query.status !== undefined) params.set("status", query.status);
  if (query.listingType !== undefined) params.set("listingType", query.listingType);
  if (query.wardCode !== undefined && query.wardCode !== "")
    params.set("wardCode", query.wardCode);
  if (query.search !== undefined && query.search !== "")
    params.set("search", query.search);
  const qs = params.toString();
  return qs ? `/projects?${qs}` : "/projects";
}

/** The grouped "Dự án" view. `enabled` gates it to grouped mode. */
export function useProjects(query: PropertyListQuery, enabled: boolean) {
  return useQuery({
    queryKey: projectKeys.list(query),
    queryFn: () => apiRequest<Project[]>(buildProjectsPath(query)),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useProject(id: number | undefined) {
  return useQuery({
    queryKey: id !== undefined ? projectKeys.detail(id) : projectKeys.details(),
    queryFn: () => apiRequest<Project>(`/projects/${id}`),
    enabled: id !== undefined,
  });
}

/** Suggested next unit code for a project, or null when it has no prefix. */
export function useProjectNextCode(id: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: id !== undefined ? projectKeys.nextCode(id) : [...projectKeys.all, "next-code", "none"],
    queryFn: () =>
      apiRequest<{ internalCode: string | null }>(`/projects/${id}/next-code`),
    enabled: enabled && id !== undefined,
  });
}

function invalidateProjectsAndProperties(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: projectKeys.all });
  // Propagation touches the units, so refresh property lists too.
  void queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectInput) =>
      apiRequest<Project>("/projects", { method: "POST", body }),
    onSuccess: () => invalidateProjectsAndProperties(queryClient),
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProjectInput) =>
      apiRequest<Project>(`/projects/${id}`, { method: "PATCH", body }),
    onSuccess: (project) => {
      queryClient.setQueryData(projectKeys.detail(id), project);
      invalidateProjectsAndProperties(queryClient);
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/projects/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateProjectsAndProperties(queryClient),
  });
}
